import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Flashcard from './Flashcard';
import './FlashcardsPage.css';
import { getWordKey, getGroupId, persistSm2SrsStateToSupabase } from '../utils/srs';
import { useLanguage } from '../contexts/LanguageContext';
import { supabase } from '../supabaseClient';
import { useData } from '../contexts/DataContext';

const StudyAllWordsPage = () => {
  const { t, isRTL } = useLanguage();
  const { id } = useParams();
  const navigate = useNavigate();
  const { modules } = useData();

  // Get user session
  useEffect(() => {
    const getSession = async () => {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      setSession(currentSession);
    };
    getSession();
  }, []);

  const [mod, setMod] = useState(null);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [activeWords, setActiveWords] = useState([]);
  const [preloadedSRSData, setPreloadedSRSData] = useState({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [sm2LastUndo, setSm2LastUndo] = useState(null);
  const [sm2UndoError, setSm2UndoError] = useState('');

  useEffect(() => {
    const fetchModuleWords = async () => {
      // Get user session
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id;
      
      if (!userId) {
        setLoading(false);
        return;
      }

      // First, try to find the module from dashboard data (minimal info)
      let currentMod = modules?.find(m => String(m.id) === String(id));

      // If not found in global state (e.g., on refresh or for classroom members), fetch it directly from Supabase
      if (!currentMod) {
        // Check if id is a UUID (contains dashes) or a position (number)
        const isUUID = typeof id === 'string' && id.includes('-');
        
        let query = supabase
          .from('modules_new')
          .select('*');
        
        if (isUUID) {
          // It's a module ID (classroom module) - RLS will handle access check
          query = query.eq('id', id);
        } else {
          // It's a position (private module)
          query = query.eq('position', Number(id))
                      .eq('user_id', userId);
        }
        
        const { data: moduleData, error: moduleError } = await query.single();

        if (moduleError) {
          console.error('Error fetching module:', moduleError);
          setLoading(false);
          return;
        }

        currentMod = moduleData;
      }

      if (!currentMod) {
        setLoading(false);
        return;
      }

      // Check access for classroom modules
      if (currentMod.classroom_id) {
        const { checkModuleAccess } = await import('../utils/checkModuleAccess');
        const accessCheck = await checkModuleAccess(currentMod.id, currentMod.classroom_id);
        if (!accessCheck.canAccess) {
          setError(accessCheck.error || 'Access to this module is blocked');
          setLoading(false);
          return;
        }
      }
      
      setMod(currentMod);

      try {
        // Fetch all word groups for this module
    
        const { data: groupsData, error: groupsError } = await supabase
          .from('word_groups')
          .select('*')
          .eq('module_id', currentMod.id)
          .order('position', { ascending: false });
        
    
        if (groupsError) {
          console.error('Error fetching word groups:', groupsError);
          setLoading(false);
          return;
        }

        // Fetch all words for all groups with pagination to get ALL words
        const groupIds = groupsData.map(g => g.id);
        let allWords = [];
        
        if (groupIds.length > 0) {
          let from = 0;
          const limit = 1000;
          let hasMore = true;
          
          while (hasMore) {
          const { data: wordsData, error: wordsError } = await supabase
            .from('words')
            .select(`
              id,
              group_id,
              from_text,
              to_text,
              position,
              created_at,
              updated_at,
              word_srs_data!left (
                sm2_interval,
                learning_interval,
                sm2_ease,
                learning_ease,
                next_due,
                apparent_tier,
                history,
                updated_at
              )
            `)
            .in('group_id', groupIds)
            .eq('word_srs_data.user_id', userId)
              .order('position', { ascending: true })
              .range(from, from + limit - 1);
      
          if (wordsError) {
            console.error('Error fetching words:', wordsError);
            setLoading(false);
            return;
          }
          
            if (wordsData && wordsData.length > 0) {
              // Merge SRS data into words
              const mergedWords = wordsData.map(word => {
                const srs = word.word_srs_data?.[0] || null;
                const sm2Ease = srs?.sm2_ease ?? 2.5;
                const learningEase = srs?.learning_ease ?? 2.5;
                return {
                  ...word,
                  word_srs_data: undefined,
                  sm2_interval: srs?.sm2_interval ?? 0,
                  learning_interval: srs?.learning_interval ?? 0,
                  sm2_ease: sm2Ease,
                  learning_ease: learningEase,
                  // Keep legacy ease alias mapped from SM2 ease for any callers that still read it
                  ease: sm2Ease,
                  next_due: srs?.next_due ?? 0,
                  apparent_tier: srs?.apparent_tier ?? 1,
                  history: srs?.history 
                    ? (typeof srs.history === 'string' ? JSON.parse(srs.history) : srs.history)
                    : []
                };
              });
              allWords.push(...mergedWords);
              from += limit;
              hasMore = wordsData.length === limit; // Continue if we got a full batch
            } else {
              hasMore = false;
            }
          }
        }

        // Organize words by group
        const wordsByGroup = [];
        groupsData.forEach((group, groupIdx) => {
          const groupWords = allWords.filter(word => word.group_id === group.id);
          groupWords.forEach(word => {
            wordsByGroup.push({ word, groupIdx });
          });
        });

        if (wordsByGroup.length === 0) {
          setActiveWords([]);
          setLoading(false);
          return;
        }

        // Filter due words using the words table data (this will be updated with fresh SRS data later)
        const now = Date.now();
    
        const dueWords = wordsByGroup.filter(({ word }) => {
          // Use next_due from the word itself (words table)
          const nextDue = word.next_due ?? 0;
          const isDue = nextDue === 0 || nextDue <= now;
          return isDue;
        });
        
        // Deduplicate due words by word ID to avoid the same card appearing twice in a row
        const uniqueDueWordsMap = new Map();
        dueWords.forEach(entry => {
          const id = entry.word?.id;
          if (id && !uniqueDueWordsMap.has(id)) {
            uniqueDueWordsMap.set(id, entry);
          }
        });
        const uniqueDueWords = Array.from(uniqueDueWordsMap.values());
    
        // Sort due words by when they are due (earliest first)
        // Use position as a secondary sort key for deterministic ordering when next_due values are equal
        const sortedDueWords = uniqueDueWords.sort((a, b) => {
          const aDue = a.word.next_due ?? 0;
          const bDue = b.word.next_due ?? 0;
          // Primary sort: by due date (earliest first)
          if (aDue !== bDue) {
            return aDue - bDue;
          }
          // Secondary sort: by position when next_due values are equal
          return (a.word.position ?? 0) - (b.word.position ?? 0);
        });

        // Preload SRS data (already merged from word_srs_data)
        const formattedSRSData = {};
        sortedDueWords.forEach(({ word, groupIdx }) => {
          const groupId = getGroupId(currentMod, groupIdx);
          const key = getWordKey(word, currentMod.id, groupId);

          const sm2Ease = word.sm2_ease ?? word.ease ?? 2.5;
          const learningEase = word.learning_ease ?? 2.5;

          formattedSRSData[key] = {
            sm2_interval: word.sm2_interval ?? 0,
            learning_interval: word.learning_interval ?? 0,
            sm2_ease: sm2Ease,
            learning_ease: learningEase,
            // Keep legacy ease alias mapped from SM2 ease for any callers that still read it
            ease: sm2Ease,
            nextDue: word.next_due ?? 0,
            history: Array.isArray(word.history) ? word.history : (word.history || []),
            apparentTier: word.apparent_tier ?? 1,
            lastUpdated: word.updated_at ? new Date(word.updated_at).getTime() : Date.now()
          };
        });

        setPreloadedSRSData(formattedSRSData);
        setActiveWords(sortedDueWords);
        setCurrentCardIndex(0);
      } catch (error) {
        console.error('Error loading due words:', error);
      }
      
      setLoading(false);
    };

    fetchModuleWords();
  }, [id, modules]);

  const handleSRSUpdate = (wordKey, updatedSRSData) => {
    setPreloadedSRSData(prev => ({
      ...prev,
      [wordKey]: updatedSRSData
    }));
    // Note: We don't re-filter due words here to prevent card flashing
    // The due word filtering happens on page load and is sufficient for the review experience
  };

  const handleNextCard = () => {
    if (activeWords.length === 0 || currentCardIndex >= activeWords.length) {
      return;
    }
    setCurrentCardIndex(prev => prev + 1);
    setIsFlipped(false);
  };

  const handleSm2RegisterUndo = useCallback((payload) => {
    setSm2LastUndo(payload);
    setSm2UndoError('');
  }, []);

  const handleSm2GoBack = useCallback(async () => {
    if (!sm2LastUndo || !session?.user?.id) return;
    const { wordKey, wordId, previousSrs, cardIndex } = sm2LastUndo;
    setSm2UndoError('');
    try {
      await persistSm2SrsStateToSupabase(wordId, session.user.id, previousSrs);
      const preloaded = {
        ...previousSrs,
        ease: previousSrs.sm2_ease ?? 2.5
      };
      setPreloadedSRSData(prev => ({ ...prev, [wordKey]: preloaded }));
      setActiveWords(prev =>
        prev.map(entry =>
          entry.word?.id === wordId
            ? {
                ...entry,
                word: {
                  ...entry.word,
                  sm2_interval: previousSrs.sm2_interval ?? 0,
                  learning_interval: previousSrs.learning_interval ?? 0,
                  sm2_ease: previousSrs.sm2_ease ?? 2.5,
                  learning_ease: previousSrs.learning_ease ?? 2.5,
                  ease: previousSrs.sm2_ease ?? 2.5,
                  next_due: previousSrs.nextDue ?? 0,
                  apparent_tier: previousSrs.apparentTier ?? 1,
                  history: previousSrs.history ?? []
                }
              }
            : entry
        )
      );
      setCurrentCardIndex(cardIndex);
      setIsFlipped(false);
      setSm2LastUndo(null);
    } catch (err) {
      console.error('SM2 undo failed:', err);
      setSm2UndoError(t('errorUpdatingProgress') || 'Failed to update your progress. Please try again.');
    }
  }, [sm2LastUndo, session?.user?.id, t]);

  useEffect(() => {
    let isProcessing = false;
    let lastKeyTime = 0;
    const DEBOUNCE_TIME = 300; // Increased debounce time further
    let processingTimeout = null;
    
    const handleKeyDown = (e) => {
      // Block ALL key events if we're processing
      if (isProcessing) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      
      // Prevent key repeat events (when holding down a key)
      if (e.repeat) {
        e.preventDefault();
        return;
      }
      
      // Prevent rapid key presses with time-based debouncing
      const now = Date.now();
      if ((now - lastKeyTime) < DEBOUNCE_TIME) {
        e.preventDefault();
        return;
      }
      
      // Set processing flag immediately
      isProcessing = true;
      lastKeyTime = now;
      
      // Clear any existing timeout
      if (processingTimeout) {
        clearTimeout(processingTimeout);
      }
      
      try {
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
          const check = !isRTL ? 'ArrowRight' : 'ArrowLeft';
          const x = !isRTL ? 'ArrowLeft' : 'ArrowRight';

          if (e.key === check) {
            const checkButton = document.querySelector('.flashcard-feedback-btn.check');
            if (checkButton) {
              checkButton.click();
            }
          } else if (e.key === x) {
            const xButton = document.querySelector('.flashcard-feedback-btn.x');
            if (xButton) {
              xButton.click();
            }
          }
        } else if (e.code === 'Space') {
          e.preventDefault();
          setIsFlipped(prev => !prev);
        }
      } catch (error) {
        console.error('Error in study key handler:', error);
      }
      
      // Set timeout to clear processing flag
      processingTimeout = setTimeout(() => { 
        isProcessing = false; 
      }, DEBOUNCE_TIME);
    };
    
    window.addEventListener('keydown', handleKeyDown, true); // Use capture phase
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
      if (processingTimeout) {
        clearTimeout(processingTimeout);
      }
    };
  }, [isRTL]);

  const noDueWordsInitial = activeWords.length === 0;
  const studySessionComplete = activeWords.length > 0 && currentCardIndex >= activeWords.length;

  if (error) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: '2rem' }}>
        <h2>{t('accessDenied') || 'Access Denied'}</h2>
        <p>{error}</p>
        <button onClick={() => navigate('/')} className="btn btn-primary">
          {t('backToDashboard') || 'Back to Dashboard'}
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flashcards-page-container">
        <div className="flashcard-view-container">
          <div className="flashcard-skeleton" style={{
            height: 320,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 24,
            color: '#aaa'
          }}>
            {t('loading') || 'Loading...'}
          </div>
        </div>
      </div>
    );
  }

  if (noDueWordsInitial) {
    return (
      <div className="flashcards-page-container">
        <div className="flashcard-view-container">
          <div className="flashcard-skeleton" style={{
            height: 320,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 24,
            color: '#aaa'
          }}>
            {t('noDueWords') || 'No due words.'}
          </div>
          <button onClick={() => navigate(`/module/${mod?.id || id}`)} className="btn btn-primary" style={{ marginTop: '1rem' }}>
            {t('returnToModule') || 'Return to Module'}
          </button>
        </div>
      </div>
    );
  }

  if (studySessionComplete) {
    return (
      <div className="flashcards-page-container">
        <button onClick={() => navigate(`/module/${mod?.id || id}`)} className="btn btn-secondary btn-back">
          {isRTL ? '→' : '←'} {t('exitFlashcards')}
        </button>
        <div className="flashcard-view-container">
          <div className="flashcard-skeleton" style={{
            height: 320,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 24,
            color: '#aaa'
          }}>
            {t('noDueWords') || 'No due words.'}
          </div>
          <button onClick={() => navigate(`/module/${mod?.id || id}`)} className="btn btn-primary" style={{ marginTop: '1rem' }}>
            {t('returnToModule') || 'Return to Module'}
          </button>
          {sm2LastUndo ? (
            <div style={{ marginTop: '0.75rem', textAlign: 'center' }}>
              <button
                type="button"
                onClick={handleSm2GoBack}
                className="btn btn-secondary"
                style={{
                  fontSize: '0.8125rem',
                  opacity: 0.75,
                  padding: '0.35rem 0.75rem'
                }}
              >
                {isRTL ? '→' : '←'} {t('sm2UndoLastCard')}
              </button>
              {sm2UndoError ? (
                <div style={{ color: 'red', marginTop: '0.5rem', fontSize: '0.85rem' }}>{sm2UndoError}</div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  const currentWordEntry = activeWords[currentCardIndex];
  if (!currentWordEntry) {
    return (
      <div className="flashcards-page-container">
        <div className="flashcard-view-container">
          <div className="flashcard-skeleton" style={{
            height: 320,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 24,
            color: '#aaa'
          }}>
            {t('noDueWords') || 'No due words.'}
          </div>
          <button onClick={() => navigate(`/module/${mod?.id || id}`)} className="btn btn-primary" style={{ marginTop: '1rem' }}>
            {t('returnToModule') || 'Return to Module'}
          </button>
        </div>
      </div>
    );
  }
  
  const currentWord = currentWordEntry.word;
  const currentGroupIdx = currentWordEntry.groupIdx;



  return (
    <div className="flashcards-page-container">
      <button onClick={() => navigate(`/module/${mod?.id || id}`)} className="btn btn-secondary btn-back">
        {isRTL ? '→' : '←'} {t('exitFlashcards')}
      </button>
      <div className="flashcard-view-container">
        <Flashcard
          key={`${currentCardIndex}-${currentWord?.id || ''}`}
          word={currentWord}
          module={mod}
          isFlipped={isFlipped}
          onFlip={() => setIsFlipped(prev => !prev)}
          moduleId={mod.id}
          groupIdx={currentGroupIdx}
          onFeedback={handleNextCard}
          onSRSUpdate={handleSRSUpdate}
          userId={session?.user?.id}
          preloadedSRSData={preloadedSRSData}
          mode="sm2"
          sm2UndoCardIndex={currentCardIndex}
          onSm2RegisterUndo={handleSm2RegisterUndo}
        />
        <div className="due-counter" style={{ fontWeight: 'bold', fontSize: '1rem', color: '#555', textAlign: 'center', direction: isRTL ? 'rtl' : 'ltr' }}>
          {t('cardsLeft') || 'Cards Left:'} {activeWords.length - currentCardIndex}
        </div>
        {sm2LastUndo ? (
          <div
            className="sm2-undo-active"
            style={{
              marginTop: '0.75rem',
              textAlign: 'center',
              direction: isRTL ? 'rtl' : 'ltr'
            }}
          >
            <button
              type="button"
              onClick={handleSm2GoBack}
              className="btn btn-secondary"
              style={{
                width: '100%',
                maxWidth: '440px',
                fontWeight: 600,
                fontSize: '1rem',
                padding: '0.7rem 1rem'
              }}
            >
              {isRTL ? '→' : '←'} {t('sm2UndoLastCard')}
            </button>
            {sm2UndoError ? (
              <div style={{ color: 'red', marginTop: '0.5rem', fontSize: '0.9rem' }}>{sm2UndoError}</div>
            ) : null}
          </div>
        ) : null}
        <div className="sm2-mode-info" style={{ marginTop: '0.75rem', direction: isRTL ? 'rtl' : 'ltr', textAlign: 'center' }}>
          {t('sm2ModeInfo') || 'These are your due words. Review each one once.'}
        </div>
      </div>
    </div>
  );
};

export default StudyAllWordsPage;
